import { useEffect, type ReactNode } from "react";
import { useNavigate, useLocation } from "@tanstack/react-router";
import { useAuthStore, type UserRole } from "@/lib/auth/store";

interface RequireAuthProps {
  children: ReactNode;
  roles?: UserRole[];
}

export function RequireAuth({ children, roles }: RequireAuthProps) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const navigate = useNavigate();

  // Use an effect-driven navigate so a re-render that still has no token
  // doesn't keep emitting `<Navigate>` elements every commit — that's what
  // caused the "Maximum update depth exceeded" loop when refresh kept failing.
  useEffect(() => {
    if (!accessToken || !user) {
      void navigate({ to: "/login", search: { from: location.pathname } as never });
    }
  }, [accessToken, user, navigate, location.pathname]);

  if (!accessToken || !user) {
    return null;
  }

  if (roles && roles.length > 0 && !roles.includes(user.role)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="rounded-lg border bg-card p-8 text-center shadow-sm">
          <h2 className="text-xl font-semibold text-destructive">Không có quyền</h2>
          <p className="mt-2 text-muted-foreground">
            Bạn không có quyền truy cập trang này.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
